import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Save, Download } from "lucide-react";

export default function Account() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = url;
      link.download = `voleivida-backup-${today}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      const totals = Object.entries(data.collections || {}).map(([k, v]) => `${k}: ${v.length}`).join(", ");
      toast.success(`Backup gerado (${totals})`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  React.useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) return toast.error("Informe sua senha atual");
    if (newPassword && newPassword.length < 6) return toast.error("Nova senha deve ter ao menos 6 caracteres");
    if (newPassword && newPassword !== confirmPassword) return toast.error("Confirmação de senha não confere");

    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (name && name !== user?.name) payload.new_name = name;
      if (email && email.toLowerCase() !== (user?.email || "").toLowerCase()) payload.new_email = email;
      if (newPassword) payload.new_password = newPassword;

      const { data } = await api.post("/auth/change-credentials", payload);
      if (data.token) localStorage.setItem("vv_token", data.token);
      toast.success("Credenciais atualizadas");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-account-page" className="max-w-2xl">
      <PageHeader
        eyebrow="Segurança"
        title="Minha conta"
        subtitle="Atualize seu nome, email e senha de acesso ao painel."
        mascot={BRAND.MANGUITA_COACH}
      />

      <div className="vv-card p-6">
        <div className="flex items-center gap-2 mb-5 text-[hsl(var(--vv-lime))]">
          <ShieldCheck className="size-5" />
          <span className="text-xs uppercase tracking-widest font-bold">Dados de acesso</span>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" />
          </div>
          <div>
            <Label>Email de login *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="account-email-input" />
          </div>

          <div className="border-t border-border pt-4 mt-2">
            <Label>Nova senha (opcional)</Label>
            <Input
              type="password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Deixe em branco para manter"
              data-testid="account-new-password-input"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label>Confirmar nova senha</Label>
            <Input
              type="password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repita a nova senha"
              data-testid="account-confirm-password-input"
              autoComplete="new-password"
            />
          </div>

          <div className="border-t border-border pt-4">
            <Label className="text-[hsl(var(--vv-yellow))]">Senha atual *</Label>
            <Input
              type="password" required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Confirme sua identidade com a senha atual"
              data-testid="account-current-password-input"
              autoComplete="current-password"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Por segurança, é necessário informar a senha atual para alterar qualquer dado.
            </p>
          </div>

          <Button type="submit" disabled={saving} data-testid="account-save-button" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider">
            <Save className="size-4 mr-2" /> {saving ? "Salvando..." : "Salvar alterações"}
          </Button>
        </form>
      </div>

      <div className="vv-card p-6 mt-6">
        <div className="flex items-center gap-2 mb-3 text-[hsl(var(--vv-yellow))]">
          <Download className="size-5" />
          <span className="text-xs uppercase tracking-widest font-bold">Backup dos dados</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Baixe um arquivo JSON com todos os dados do sistema (alunos, turmas, presenças,
          avaliações, avisos e financeiro). Guarde em local seguro — recomendado antes de qualquer
          alteração crítica ou redeploy.
        </p>
        <Button
          type="button" onClick={downloadBackup} disabled={downloading}
          data-testid="download-backup-button"
          className="bg-[hsl(var(--vv-blue))] font-bold uppercase tracking-wider"
        >
          <Download className="size-4 mr-2" /> {downloading ? "Gerando..." : "Baixar backup completo (.json)"}
        </Button>
      </div>
    </div>
  );
}
